/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */



function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("cart-recommendations");
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}
customElements.define("cart-recommendations", CartRecommendations);

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback(){
      this.form = this;
      this.form.querySelector('[name=id]').disabled = false;
      this.form.querySelector('[data-action="add-to-cart"]').addEventListener('click', this.onSubmitHandler.bind(this));
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      const submitButton = this.querySelector('[data-action="add-to-cart"]');
      if (submitButton.classList.contains('loading')) return;

      this.handleErrorMessage();

      submitButton.setAttribute('aria-disabled', true);
      submitButton.classList.add('loading');
      document.dispatchEvent(new CustomEvent('theme:loading:start')); // Then we add the product in Ajax

      var inputValue = this.form.querySelector('[name=id]').value;
      
      let formData = {
        'items': [{
         'id': inputValue
         }]
       };
       
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }).then((response) => response.json())
        .then((response) => {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          if (window.theme.cartType === 'drawer') {
            var quantityElement = this.form.querySelector('[name="quantity"]');
            // We simply trigger an event so the mini-cart can re-render
            this.dispatchEvent(new CustomEvent('product:added', {
              bubbles: true,
              detail: {
                quantity: quantityElement ? parseInt(quantityElement.value) : 1
              }
            }));
          }
        
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

class FreeGiftCart extends HTMLElement {
  constructor() {
    super();
    // Store the gift ID from the dataset for later use
    this.giftId = this.dataset.id;
  }

  // Called when the element is inserted into the DOM
  connectedCallback() {
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    this.addToCart();
  }

  /**
   * Initiates a fetch request to add the gift item to the cart.
   */
  async addToCart() {
    const formData = this.createFormData();

    try {
      const response = await this.sendAddToCartRequest(formData);
      this.handleResponse(response);
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  /**
   * Creates the form data for the fetch request.
   * @returns {Object} The form data object.
   */
  createFormData() {
    return {
      items: [
        { id: this.giftId, 
          properties: {
            "_Is Gift": this.giftId
          }
        }
      ]
    };
  }

  /**
   * Sends a POST request to add an item to the cart.
   * @param {Object} formData - The data to send in the request.
   * @returns {Promise<Response>} The fetch response.
   */
  async sendAddToCartRequest(formData) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Handles the response from the fetch request.
   * @param {Object} response - The response from the server.
   */
  handleResponse(response) {
    document.dispatchEvent(new CustomEvent('theme:loading:end'));

    // Check for errors in the response
    if (response.errors) {
      console.error('Error details:', response.errors);
      // Optionally display error messages to the user
      return;
    }

    // Dispatch an event to update the cart UI if applicable
    if (window.theme.cartType === 'drawer') {
      this.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { quantity: 1 }
      }));
    }
  }
}

// Define the custom element
customElements.define("free-gift", FreeGiftCart);

class RemoveGiftCart extends HTMLElement {
  constructor() {
    super();
    // Store the gift ID and quantity from the dataset
    this.giftId = this.dataset.id;
    this.quantity = this.dataset.quantity;
    this.hasGiftAdded = this.dataset.gift === 'true'; // Convert to boolean
  }

  // Called when the element is inserted into the DOM
  connectedCallback() {
    if (this.hasGiftAdded) {
      document.dispatchEvent(new CustomEvent('theme:loading:start'));
      this.removeFromCart();
    }
  }

  /**
   * Initiates a fetch request to remove the gift item from the cart.
   */
  async removeFromCart() {
    const formData = this.createFormData();

    try {
      const response = await this.sendRemoveFromCartRequest(formData);
      this.handleResponse(response);
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }

  /**
   * Creates the form data for the fetch request.
   * @returns {Object} The form data object.
   */
  createFormData() {
    return {
      id: this.giftId,
      quantity: this.quantity
    };
  }

  /**
   * Sends a POST request to remove an item from the cart.
   * @param {Object} formData - The data to send in the request.
   * @returns {Promise<Response>} The fetch response.
   */
  async sendRemoveFromCartRequest(formData) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Handles the response from the fetch request.
   * @param {Object} response - The response from the server.
   */
  handleResponse(response) {
    document.dispatchEvent(new CustomEvent('theme:loading:end'));

    // Check for errors in the response
    if (response.errors) {
      console.error('Error details:', response.errors);
      // Optionally display error messages to the user
      return;
    }

    // Dispatch an event to update the cart UI if applicable
    if (window.theme.cartType === 'drawer') {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));
    }
  }
}

// Define the custom element
customElements.define("remove-gift", RemoveGiftCart);